import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { updateVisitorWorkflow, updateVisitorWorkflowConfig } from 'store/slices/visitorWorkflowsSlice';
import { List } from 'antd';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { OpRow } from 'components/customAntd/DLS/OpRow/OpRow';
import { OpCol } from 'components/customAntd/DLS/OpCol/OpCol';
import locationIcon from '../../location.svg';
import { hasPermission } from 'utils/utils';
import { useTranslation } from 'react-i18next';

interface WorkflowContentProps {
    form: any;
}

const WorkflowContent: React.FC<WorkflowContentProps> = ({ form }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisitorWorkflow, selectedVisitorWorkflowLocations: visitorWorkflowLocations, selectedSignInWorkflowConfig: visitorWorkflowConfig } = useSelector((state: RootState) => state.visitorWorkflows);
    const [isVisitorPhotoEachDisabled, setVisitorPhotoEachDisabled] = useState(true);

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasSigninconfRead = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:r');
    const hasSigninconfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:w');
    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

    const { t } = useTranslation();

    const visitorInfoData = [
        { name: 'additionalVisitors', label: t('workflow.kioskSignIn.form.additionalVisitors'), valuePropName: 'checked' },
        { name: 'visitorPhoto', label: t('workflow.kioskSignIn.form.visitorPhoto'), valuePropName: 'checked' },
        { name: 'visitorPhotoEach', label: t('workflow.kioskSignIn.form.visitorPhotoEach'), valuePropName: 'checked', dependent: true },
    ];

    const notificationData = [
        { name: 'notifyHost', label: t('workflow.kioskSignIn.form.notifyHost'), valuePropName: 'checked' },
    ];

    const redFlagData = [
        { name: 'checkRedFlag', label: t('workflow.kioskSignIn.form.checkRedFlag'), valuePropName: 'checked' },
    ];


    useEffect(() => {
        const initialVisitorPhotoState = visitorWorkflowConfig?.visitorPhoto;
        setVisitorPhotoEachDisabled(!initialVisitorPhotoState);
        if (!initialVisitorPhotoState) {
            form.setFieldsValue({ visitorPhotoEach: false });
        }
    }, [visitorWorkflowConfig, form]);

    const initialValues = useMemo(() => {
        if (selectedVisitorWorkflow) {
            return {
                name: selectedVisitorWorkflow.name,
                workflowType: t('workflow.kioskSignIn.workflowType'),
                additionalVisitors: visitorWorkflowConfig?.additionalVisitors,
                visitorPhoto: visitorWorkflowConfig?.visitorPhoto,
                visitorPhotoEach: visitorWorkflowConfig?.visitorPhotoEach,
                notifyHost: visitorWorkflowConfig?.notifyHost,
                checkRedFlag: visitorWorkflowConfig?.checkRedFlag,
            };
        }
        return {};
    }, [selectedVisitorWorkflow, visitorWorkflowConfig, t]);

    const handleSubmit = useCallback(async ({ values, touchedValues, initialValues }: IOnSubmitArgs<any>) => {
        const visitorWorkflowConfigParams = {
            additionalVisitors: values.additionalVisitors ? 1 : 0,
            visitorPhoto: values.visitorPhoto ? 1 : 0,
            visitorPhotoEach: values.visitorPhotoEach ? 1 : 0,
            notifyHost: values.notifyHost ? 1 : 0,
            checkRedFlag: values.checkRedFlag ? 1 : 0,
        };

        if (selectedVisitorWorkflow) {
            await dispatch(updateVisitorWorkflow({ orgId, visitorWorkflowId: selectedVisitorWorkflow.id, visitorWorkflow: { name: touchedValues.name } })).unwrap();
            await dispatch(updateVisitorWorkflowConfig({ orgId, visitorWorkflowId: selectedVisitorWorkflow.id, visitorWorkflowConfigParams })).unwrap();
        }
    }, [dispatch, orgId, selectedVisitorWorkflow]);

    const handleVisitorPhotoChange = (checked: boolean) => {
        if (checked === false) {
            setVisitorPhotoEachDisabled(true);
            form.setFieldsValue({ visitorPhotoEach: false });
        } else {
            setVisitorPhotoEachDisabled(false);
        }
    };

    const locationIconStyle = {
        marginRight: '8px',
        width: '16px',
        height: '16px',
        filter: isDarkMode ? 'invert(1)' : 'none',
    };

    return (
        <>
            <OpForm
                form={form}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                hasError={false}
                defaultButtons={false}
                isReadOnly={!hasSigninconfWrite && hasSigninconfRead}
            >
                <OpSpace
                    direction="vertical"
                    size="middle"
                    style={{
                        display: 'flex',
                    }}
                >
                    <OpRow gutter={16}>
                        <OpCol span={12}>
                            <OpForm.Input label={t('workflow.kioskSignIn.form.name')} name="name" rules={[{ required: true, message: t('workflow.kioskSignIn.form.nameRequired') }]} />
                        </OpCol>
                        <OpCol span={12}>
                            <OpForm.Input disabled label={t('workflow.kioskSignIn.form.workflowType')} name="workflowType" />
                        </OpCol>
                    </OpRow>

                    <List
                        header={t('workflow.kioskSignIn.form.visitorInformationTitle')}
                        dataSource={visitorInfoData}
                        bordered
                        renderItem={item => (
                            <List.Item>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <OpForm.Switch
                                        name={item.name}
                                        style={{ marginBottom: 0 }}
                                        disabled={item.dependent && (isVisitorPhotoEachDisabled || (!hasSigninconfWrite && hasSigninconfRead))}
                                        onChange={item.name === 'visitorPhoto' ? handleVisitorPhotoChange : undefined}
                                    />
                                    <span style={{ marginLeft: 8 }}>{item.label}</span>
                                </div>
                            </List.Item>
                        )}
                    />

                    <List
                        header={t('workflow.kioskSignIn.form.notificationTitle')}
                        dataSource={notificationData}
                        bordered
                        renderItem={item => (
                            <List.Item>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <OpForm.Switch
                                        name={item.name}
                                        style={{ marginBottom: 0 }}
                                        disabled={!hasSigninconfWrite && hasSigninconfRead}
                                    />
                                    <span style={{ marginLeft: 8 }}>{item.label}</span>
                                </div>
                            </List.Item>
                        )}
                    />

                    <List
                        header={t('workflow.kioskSignIn.form.redFlagsTitle')}
                        dataSource={redFlagData}
                        bordered
                        renderItem={item => (
                            <List.Item>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <OpForm.Switch
                                        name={item.name}
                                        style={{ marginBottom: 0 }}
                                        disabled={!hasSigninconfWrite && hasSigninconfRead}
                                    />
                                    <span style={{ marginLeft: 8 }}>{item.label}</span>
                                </div>
                            </List.Item>
                        )}
                    />


                    <List
                        header={t('workflow.kioskSignIn.form.locationsTitle')}
                        bordered
                        dataSource={visitorWorkflowLocations.data.filter(location => location.status === 1)}
                        renderItem={location => (
                            <List.Item>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <img src={locationIcon} alt="location icon" style={locationIconStyle} />
                                    <span>{location.name}</span>
                                </div>
                            </List.Item>
                        )}
                    />
                </OpSpace>
            </OpForm>
        </>
    );
};

export default WorkflowContent;
